import { useState, useRef, useEffect, useCallback } from "react";
import { Play, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  trackProductTourClick,
  trackDemoPosterOpen,
  trackDemoPlay,
  trackDemoComplete,
  trackDemoProgress,
} from "../hooks/useMarketingAnalytics";

/**
 * Premium 16:9 video demo component.
 *
 * • Never autoplays with sound. The user must intentionally press Play.
 * • The <video> element is only mounted after the first Play click so we
 *   don't preload large media on initial page load.
 * • Fully keyboard-accessible: Enter/Space to open, Esc/backdrop/close to
 *   dismiss. Body scroll is locked while the modal is open.
 * • Ready to accept MP4 + WebM <source> elements and a WebVTT captions
 *   track. When `sources` is empty, the poster links to the product tour.
 */
export default function DemoPlayer({
  poster,
  posterAlt = "ClientDesk VA product demo",
  sources = [],
  captionsSrc,
  duration = 75,
}) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [videoMounted, setVideoMounted] = useState(false);
  const videoRef = useRef(null);
  const progressReached = useRef({ 25: false, 50: false, 75: false });

  const hasVideo = sources.length > 0;

  const handleOpen = useCallback(() => {
    if (sources.length === 0) {
      trackProductTourClick("demo_poster");
      navigate("/product-tour");
      return;
    }
    setOpen(true);
    setVideoMounted(true);
    // Fire poster_open on every modal open. The actual `marketing_demo_play`
    // event is only fired below when a real <video> element begins playback.
    trackDemoPosterOpen({ has_video: sources.length > 0 });
  }, [sources.length, navigate]);

  const handleClose = useCallback(() => {
    if (videoRef.current) {
      try {
        videoRef.current.pause();
      } catch (_) {}
    }
    setOpen(false);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") handleClose();
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, handleClose]);

  const handleTimeUpdate = () => {
    const v = videoRef.current;
    if (!v || !v.duration || Number.isNaN(v.duration)) return;
    const pct = (v.currentTime / v.duration) * 100;
    [25, 50, 75].forEach((mark) => {
      if (pct >= mark && !progressReached.current[mark]) {
        progressReached.current[mark] = true;
        trackDemoProgress(mark);
      }
    });
  };

  return (
    <>
      <button
        type="button"
        onClick={handleOpen}
        aria-label={hasVideo ? `Watch the ClientDesk VA product demo, approximately ${duration} seconds` : "Take the ClientDesk VA product tour"}
        data-testid="demo-player-poster"
        className="group relative w-full block aspect-video overflow-hidden rounded-3xl border border-[#DED4C4] bg-[#22384F] shadow-[0_35px_70px_-30px_rgba(34,56,79,0.55)] focus:outline-none focus-visible:ring-4 focus-visible:ring-[#E8712C]/40"
      >
        <img
          src={poster}
          alt={posterAlt}
          loading="lazy"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
        />
        <span
          aria-hidden="true"
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(60% 55% at 50% 55%, rgba(15,25,40,0) 0%, rgba(15,25,40,0.55) 100%)",
          }}
        />
        <span
          aria-hidden="true"
          className="absolute inset-0 flex items-center justify-center"
        >
          <span className="relative flex items-center justify-center w-[76px] h-[76px] sm:w-[92px] sm:h-[92px] rounded-full bg-white/95 backdrop-blur-md shadow-[0_20px_45px_-10px_rgba(15,25,40,0.65)] transition-transform duration-300 group-hover:scale-110">
            <span className="absolute inset-0 rounded-full bg-white/60 animate-ping opacity-30" />
            <Play
              size={30}
              strokeWidth={0}
              fill="#22384F"
              className="text-[#22384F] translate-x-[2px]"
            />
          </span>
        </span>
        <span className="absolute bottom-4 left-4 sm:bottom-5 sm:left-5 inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/92 backdrop-blur text-[12px] font-semibold text-[#1A2A3A]">
          <span className="w-1.5 h-1.5 rounded-full bg-[#E8712C]" />
          {hasVideo ? `${duration}-second demo · No signup required` : "Product tour · No signup required"}
        </span>
      </button>

      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="ClientDesk VA product demo"
          data-testid="demo-player-modal"
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 sm:p-8"
          onClick={handleClose}
        >
          <div
            className="absolute inset-0 bg-[#0F1926]/85 backdrop-blur-sm"
            aria-hidden="true"
          />
          <div
            className="relative w-full max-w-5xl aspect-video rounded-2xl overflow-hidden bg-black shadow-[0_50px_100px_-30px_rgba(0,0,0,0.8)]"
            onClick={(e) => e.stopPropagation()}
          >
            {hasVideo && videoMounted && (
              <video
                ref={videoRef}
                className="w-full h-full"
                controls
                autoPlay
                playsInline
                preload="metadata"
                poster={poster}
                onPlay={() => trackDemoPlay()}
                onEnded={() => trackDemoComplete()}
                onTimeUpdate={handleTimeUpdate}
                data-testid="demo-player-video"
              >
                {sources.map((s) => (
                  <source key={s.src} src={s.src} type={s.type} />
                ))}
                {captionsSrc && (
                  <track
                    kind="captions"
                    label="English"
                    srcLang="en"
                    src={captionsSrc}
                    default
                  />
                )}
                Your browser does not support HTML5 video.
              </video>
            )}
          </div>
          <button
            type="button"
            aria-label="Close demo"
            onClick={handleClose}
            data-testid="demo-player-close"
            className="absolute top-4 right-4 sm:top-6 sm:right-6 z-10 w-10 h-10 rounded-full bg-white/95 hover:bg-white text-[#22384F] flex items-center justify-center shadow-lg"
          >
            <X size={20} />
          </button>
        </div>
      )}
    </>
  );
}
